import { KinesisVideo } from '@aws-sdk/client-kinesis-video';
import { KinesisVideoSignaling } from '@aws-sdk/client-kinesis-video-signaling';
import { Role, SignalingClient } from 'amazon-kinesis-video-streams-webrtc';

const SignalingClientState = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
};

export class KVWebRTCService {
  viewer = {};
  iceState = 'New';

  async startViewer(
    localView,
    remoteView,
    formValues,
    onStatsReport,
    onRemoteDataMessage,
    onSignalingClientStateChange = () => {},
    onIceConnectionStateChange = () => {}
  ) {
    this.viewer.localView = localView;
    this.viewer.remoteView = remoteView;

    // Create KVS client
    const kinesisVideoClient = new KinesisVideo({
      region: formValues.region,

      credentials: {
        accessKeyId: formValues.accessKeyId,
        secretAccessKey: formValues.secretAccessKey,
        sessionToken: formValues.sessionToken,
      },

      endpoint: formValues.endpoint,
    });

    // Get signaling channel ARN
    const describeSignalingChannelResponse =
      await kinesisVideoClient.describeSignalingChannel({
        ChannelName: formValues.channelName,
      });
    const channelARN = describeSignalingChannelResponse.ChannelInfo.ChannelARN;
    console.log('[VIEWER] Channel ARN: ', channelARN);

    // Get signaling channel endpoints
    const getSignalingChannelEndpointResponse =
      await kinesisVideoClient.getSignalingChannelEndpoint({
        ChannelARN: channelARN,
        SingleMasterChannelEndpointConfiguration: {
          Protocols: ['WSS', 'HTTPS'],
          Role: Role.VIEWER,
        },
      });
    const endpointsByProtocol =
      getSignalingChannelEndpointResponse.ResourceEndpointList.reduce(
        (endpoints, endpoint) => {
          endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint;
          return endpoints;
        },
        {}
      );
    console.log('[VIEWER] Endpoints: ', endpointsByProtocol);

    const kinesisVideoSignalingChannelsClient = new KinesisVideoSignaling({
      region: formValues.region,

      credentials: {
        accessKeyId: formValues.accessKeyId,
        secretAccessKey: formValues.secretAccessKey,
        sessionToken: formValues.sessionToken,
      },

      endpoint: endpointsByProtocol.HTTPS,
    });

    // Get ICE server configuration
    const getIceServerConfigResponse =
      await kinesisVideoSignalingChannelsClient.getIceServerConfig({
        ChannelARN: channelARN,
      });
    const iceServers = [];
    if (!formValues.natTraversalDisabled && !formValues.forceTURN) {
      iceServers.push({
        urls: `stun:stun.kinesisvideo.${formValues.region}.amazonaws.com:443`,
      });
    }
    if (!formValues.natTraversalDisabled) {
      getIceServerConfigResponse.IceServerList.forEach((iceServer) =>
        iceServers.push({
          urls: iceServer.Uris,
          username: iceServer.Username,
          credential: iceServer.Password,
        })
      );
    }
    console.log('[VIEWER] ICE servers: ', iceServers);

    // Create Signaling Client
    this.viewer.signalingClient = new SignalingClient({
      channelARN,
      channelEndpoint: endpointsByProtocol.WSS,
      clientId: formValues.clientId,
      role: Role.VIEWER,
      region: formValues.region,
      credentials: {
        accessKeyId: formValues.accessKeyId,
        secretAccessKey: formValues.secretAccessKey,
        sessionToken: formValues.sessionToken,
      },
      systemClockOffset: kinesisVideoClient.config.systemClockOffset,
    });

    const resolution = formValues.widescreen
      ? { width: { ideal: 1280 }, height: { ideal: 720 } }
      : { width: { ideal: 640 }, height: { ideal: 480 } };
    const constraints = {
      video: formValues.sendVideo ? resolution : false,
      audio: formValues.sendAudio,
    };
    const configuration = {
      iceServers,
      iceTransportPolicy: formValues.forceTURN ? 'relay' : 'all',
    };
    this.viewer.peerConnection = new RTCPeerConnection(configuration);
    if (formValues.openDataChannel) {
      this.viewer.dataChannel =
        this.viewer.peerConnection.createDataChannel('kvsDataChannel');
      this.viewer.peerConnection.ondatachannel = (event) => {
        event.channel.onmessage = onRemoteDataMessage;
      };
    }

    // Poll for connection stats
    this.viewer.peerConnectionStatsInterval = setInterval(
      () => this.viewer.peerConnection.getStats().then(onStatsReport),
      1000
    );

    this.viewer.signalingClient.on('open', async () => {
      console.log('[VIEWER] Connected to signaling service');
      onSignalingClientStateChange(this.viewer.signalingClient.readyState);
      // Get a stream from the webcam, add it to the peer connection, and display it in the local view.
      // If no video/audio needed, no need to request for the sources.
      // Otherwise, the browser will throw an error saying that either video or audio has to be enabled.
      if (formValues.sendVideo || formValues.sendAudio) {
        try {
          this.viewer.localStream = await navigator.mediaDevices.getUserMedia(
            constraints
          );
          this.viewer.localStream
            .getTracks()
            .forEach((track) =>
              this.viewer.peerConnection.addTrack(
                track,
                this.viewer.localStream
              )
            );
          localView.srcObject = this.viewer.localStream;
        } catch (e) {
          console.error('[VIEWER] Could not find webcam');
          return;
        }
      }

      // Create an SDP offer to send to the master
      console.log('[VIEWER] Creating SDP offer');
      await this.viewer.peerConnection.setLocalDescription(
        await this.viewer.peerConnection.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
        })
      );

      // When trickle ICE is enabled, send the offer now and then send ICE candidates as they are generated. Otherwise wait on the ICE candidates.
      if (formValues.useTrickleICE) {
        console.log('[VIEWER] Sending SDP offer');
        this.viewer.signalingClient.sendSdpOffer(
          this.viewer.peerConnection.localDescription
        );
      }
      console.log('[VIEWER] Generating ICE candidates');
    });

    this.viewer.signalingClient.on('sdpAnswer', async (answer) => {
      // Add the SDP answer to the peer connection
      console.log('[VIEWER] Received SDP answer');
      await this.viewer.peerConnection.setRemoteDescription(answer);
    });

    this.viewer.signalingClient.on('iceCandidate', (candidate) => {
      // Add the ICE candidate received from the MASTER to the peer connection
      console.log('[VIEWER] Received ICE candidate');
      this.viewer.peerConnection.addIceCandidate(candidate);
    });

    this.viewer.signalingClient.on('close', () => {
      onSignalingClientStateChange(SignalingClientState.CLOSED);
      console.log('[VIEWER] Disconnected from signaling channel');
    });

    this.viewer.signalingClient.on('error', (error) => {
      onSignalingClientStateChange(this.viewer.signalingClient.readyState);
      console.error('[VIEWER] Signaling client error: ', error);
    });

    // Listen to ice connection state change
    this.viewer.peerConnection.addEventListener(
      'iceconnectionstatechange',
      (event) => {
        const { connectionState, iceConnectionState } = event.target;
        console.log(
          'iceconnectionstatechange (connectionState)',
          connectionState
        );
        console.log(
          'iceconnectionstatechange (iceConnectionState)',
          iceConnectionState
        );
        if (connectionState === 'new' && iceConnectionState === 'checking') {
          this.iceState = 'New';
        } else if (
          connectionState === 'connecting' &&
          iceConnectionState === 'connected'
        ) {
          this.iceState = 'Connected';
        } else if (
          connectionState === 'connected' &&
          iceConnectionState === 'disconnected'
        ) {
          this.iceState = 'Disconnected';
        } else {
          this.iceState = 'Unknown';
        }
        onIceConnectionStateChange({ iceState: this.iceState });
      }
    );

    // Send any ICE candidates to the other peer
    this.viewer.peerConnection.addEventListener(
      'icecandidate',
      ({ candidate }) => {
        onSignalingClientStateChange(this.viewer.signalingClient.readyState);
        console.log;
        if (candidate) {
          console.log('[VIEWER] Generated ICE candidate');

          // When trickle ICE is enabled, send the ICE candidates as they are generated.
          if (formValues.useTrickleICE) {
            console.log('[VIEWER] Sending ICE candidate');
            this.viewer.signalingClient.sendIceCandidate(candidate);
          }
        } else {
          console.log('[VIEWER] All ICE candidates have been generated');

          // When trickle ICE is disabled, send the offer now that all the ICE candidates have ben generated.
          if (!formValues.useTrickleICE) {
            console.log('[VIEWER] Sending SDP offer');
            this.viewer.signalingClient.sendSdpOffer(
              this.viewer.peerConnection.localDescription
            );
          }
        }
      }
    );

    // As remote tracks are received, add them to the remote view
    this.viewer.peerConnection.addEventListener('track', (event) => {
      console.log('[VIEWER] Received remote track');
      if (remoteView.srcObject) {
        return;
      }
      this.viewer.remoteStream = event.streams[0];
      remoteView.srcObject = this.viewer.remoteStream;
    });

    console.log('[VIEWER] Starting viewer connection');
    this.viewer.signalingClient.open();
  }

  stopViewer() {
    console.log('[VIEWER] Stopping viewer connection');
    if (this.viewer.signalingClient) {
      this.viewer.signalingClient.close();
      this.viewer.signalingClient = null;
    }

    if (this.viewer.peerConnection) {
      this.viewer.peerConnection.close();
      this.viewer.peerConnection = null;
    }

    if (this.viewer.localStream) {
      this.viewer.localStream.getTracks().forEach((track) => track.stop());
      this.viewer.localStream = null;
    }

    if (this.viewer.remoteStream) {
      this.viewer.remoteStream.getTracks().forEach((track) => track.stop());
      this.viewer.remoteStream = null;
    }

    if (this.viewer.peerConnectionStatsInterval) {
      clearInterval(this.viewer.peerConnectionStatsInterval);
      this.viewer.peerConnectionStatsInterval = null;
    }

    if (this.viewer.localView) {
      this.viewer.localView.srcObject = null;
    }

    if (this.viewer.remoteView) {
      this.viewer.remoteView.srcObject = null;
    }

    if (this.viewer.dataChannel) {
      this.viewer.dataChannel = null;
    }
  }

  sendViewerMessage(message) {
    if (this.viewer.dataChannel) {
      try {
        this.viewer.dataChannel.send(message);
      } catch (e) {
        console.error('[VIEWER] Send DataChannel: ', e.toString());
      }
    }
  }
}
